import React, { useEffect, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
} from '@mui/material';
import { settingsApi } from '../services/api';

/** Generic-settings key the mobile app reads to decide whether to show the
 *  "Become a Pro" onboarding entry. Absent ⇒ OFF. */
export const MOBILE_PRO_ONBOARDING_KEY = 'mobile.proOnboarding.enabled';

/**
 * GLOBAL (not per-provider) toggle: controls whether the mobile app shows the
 * "Become a Pro" onboarding entry. Pro onboarding is moving to the web dashboard,
 * so this defaults hidden; flip it on to re-expose the in-app wizard.
 *
 * Backed by the generic settings store (`settingsApi`). Deliberately NOT part of
 * the per-provider admin-actions cluster — it's app-wide.
 */
export default function MobileProOnboardingToggle() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await settingsApi.getByKey(MOBILE_PRO_ONBOARDING_KEY);
        if (!cancelled) setEnabled(s?.value === 'true');
      } catch (e) {
        // Absent (404) or unreadable ⇒ treat as OFF; not an error worth surfacing.
        if (!cancelled) setEnabled(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (e) => {
    const next = e.target.checked;
    const previous = enabled;
    setEnabled(next); // optimistic
    setSaving(true);
    setError('');
    try {
      await settingsApi.upsert(MOBILE_PRO_ONBOARDING_KEY, next ? 'true' : 'false', 'BOOLEAN');
    } catch (err) {
      setEnabled(previous); // revert
      setError('Failed to update the setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" color="text.secondary">Mobile app</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggle}
              disabled={loading || saving}
            />
          }
          label="Show Pro onboarding in the mobile app"
        />
        {(loading || saving) && <CircularProgress size={16} />}
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Paper>
  );
}
